import { Typography, Link, type LinkProps } from "@fullscript/aviary-web";
import type { MDXProvider } from "@mdx-js/react";
import type { ComponentProps } from "react";

import { formatID } from "@/utils/formatId";
import * as styles from "./MDXComponents.styles";
import { Blockquote } from "./Blockquote/Blockquote";
import { Callout } from "./Callout/Callout";
import { CustomCode } from "../CustomCode/CustomCode";
import { InlineCode } from "../InlineCode/InlineCode";
import { LabOrderLifeCycleTable } from "./LabOrderLifeCycleTable/LabOrderLifeCycleTable";
import { LabOrderUpdatedEventTable } from "./LabOrderUpdatedEventTable/LabOrderUpdatedEventTable";
import { AiCopyPasteBlock } from "@/components/AiCopyPasteBlock/AiCopyPasteBlock";
import { IntegrationEnvironmentsTable } from "@/components/IntegrationEnvironmentsTable/IntegrationEnvironmentsTable";

type ElementProps = { children: string; className?: string };
type TypographyProps = ComponentProps<typeof Typography>;

const h1 = (props: ElementProps) => (
  <Typography variant="h1" id={formatID(props.children)} {...props} />
);
const h2 = (props: ElementProps) => (
  <Typography customCss={styles.heading} variant="h2" id={formatID(props.children)} {...props} />
);
const h3 = (props: ElementProps) => (
  <Typography customCss={styles.heading} variant="h3" id={formatID(props.children)} {...props} />
);
const h4 = (props: ElementProps) => (
  <Typography customCss={styles.subHeading} variant="h4" id={formatID(props.children)} {...props} />
);
const strong = (props: ElementProps) => <strong css={styles.strong} {...props} />;

const p = (props: TypographyProps) => <Typography {...props} />;

const CustomLink = (props: LinkProps) => <Link intention="neutral" {...props} />;

const ul = (props: ElementProps) => {
  const { className } = props;
  const isTaskList = className?.includes("contains-task-list");
  return <ul css={isTaskList ? null : styles.ul} {...props} />;
};
const ol = (props: ElementProps) => <ol css={styles.ol} {...props} />;
const li = (props: ElementProps) => <li css={styles.li} {...props} />;

const td = (props: ElementProps) => <td css={styles.td} {...props} />;

const th = (props: ElementProps) => <th css={styles.th} {...props} />;

const table = (props: ComponentProps<"table">) => (
  <div css={styles.tableScrollWrapper}>
    <table css={styles.table} {...props} />
  </div>
);

const Spacer = () => <br />;

const MDXComponents = {
  h1,
  h2,
  h3,
  h4,
  p,
  a: CustomLink,
  hr: Spacer,
  ul,
  ol,
  li,
  blockquote: Blockquote,
  strong,
  table,
  th,
  td,
  code: CustomCode,
  InlineCode,
  Callout,
  LabOrderLifeCycleTable,
  LabOrderUpdatedEventTable,
  AiCopyPasteBlock,
  IntegrationEnvironmentsTable,
} as ComponentProps<typeof MDXProvider>["components"];

export { MDXComponents };
